import { useCallback, useEffect, useRef, useState } from "react";
import {
    generateSalesStrategy,
    getSalesStrategy,
    getSalesStrategyById,
    SalesStrategyData,
} from "@apiHooks/useSalesStrategy";
import {
    SSB_POLL_INTERVAL_MS,
    SSB_POLL_MAX_ATTEMPTS,
    SSB_TRIGGER_ERROR,
    SSB_POLL_ERROR,
    SSB_TIMEOUT_ERROR,
} from "./Strategy.constants";

export interface StrategyGeneration {
    data: SalesStrategyData | null;
    isGenerating: boolean;
    errorMessage: string | null;
    generate: (userInput: string | null) => void;
    resumePolling: () => void;
}

type StrategyFetcher = () => Promise<SalesStrategyData>;

interface AbortableRequest {
    abort?: () => void;
}

export function useStrategyGeneration(chainId: string | null): StrategyGeneration {
    const [data, setData] = useState<SalesStrategyData | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const requestRef = useRef<AbortableRequest | null>(null);
    const attemptsRef = useRef(0);
    const generationIdRef = useRef(0);

    const poll = useCallback((fetcher: StrategyFetcher, generationId: number) => {
        const request = fetcher();
        requestRef.current = request as AbortableRequest;

        request
            .then((result) => {
                if (generationIdRef.current !== generationId) return;
                requestRef.current = null;

                const hasSections = result.sections.length > 0;
                if (hasSections || result.state === 'Ready' || result.state === 'NotAvailable') {
                    setData(result);
                    setIsGenerating(false);
                }

                if (result.state === 'Ready' && !result.isPartial) {
                    setErrorMessage(null);
                    return;
                }

                if (result.state === 'NotAvailable') {
                    return;
                }

                attemptsRef.current += 1;
                if (attemptsRef.current >= SSB_POLL_MAX_ATTEMPTS) {
                    setIsGenerating(false);
                    setErrorMessage(SSB_TIMEOUT_ERROR);
                    return;
                }

                // eslint-disable-next-line react-hooks/exhaustive-deps -- recursive setTimeout closes over `fetcher`; deps stay empty since this callback's identity never needs to change
                timeoutRef.current = setTimeout(() => poll(fetcher, generationId), SSB_POLL_INTERVAL_MS);
            })
            .catch((error) => {
                if (generationIdRef.current !== generationId) return;
                requestRef.current = null;
                console.error(error);
                setIsGenerating(false);
                setErrorMessage(SSB_POLL_ERROR);
            });
    }, []);

    const startGeneration = useCallback(() => {
        requestRef.current?.abort?.();
        requestRef.current = null;

        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        generationIdRef.current += 1;
        attemptsRef.current = 0;
        setData(null);
        setErrorMessage(null);
        setIsGenerating(true);

        return generationIdRef.current;
    }, []);

    const generate = useCallback((userInput: string | null) => {
        if (chainId == null) return;

        const generationId = startGeneration();

        const request = generateSalesStrategy(Number(chainId), userInput);
        requestRef.current = request as AbortableRequest;

        request
            .then((strategyId) => {
                if (generationIdRef.current !== generationId) return;
                requestRef.current = null;
                poll(() => getSalesStrategyById(strategyId), generationId);
            })
            .catch((error) => {
                if (generationIdRef.current !== generationId) return;
                requestRef.current = null;
                console.error(error);
                setIsGenerating(false);
                setErrorMessage(SSB_TRIGGER_ERROR);
            });
    }, [chainId, poll, startGeneration]);

    const resumePolling = useCallback(() => {
        if (chainId == null) return;

        const generationId = startGeneration();

        poll(() => getSalesStrategy(chainId), generationId);
    }, [chainId, poll, startGeneration]);

    useEffect(() => {
        return () => {
            generationIdRef.current += 1;

            if (timeoutRef.current !== null) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            requestRef.current?.abort?.();
            requestRef.current = null;
        };
    }, []);

    return {
        data,
        isGenerating,
        errorMessage,
        generate,
        resumePolling,
    };
}
